export type MyConnectionProvider = 'gmail' | 'slack' | 'google_calendar';
export type MyConnectionStatus = 'active' | 'revoked' | 'error';
export type MyConnectionVisibility = 'private' | 'shared';

export interface MyConnectionConnectProvider {
  id: 'google' | 'slack';
  endpoint: '/api/connected-accounts/google/connect' | '/api/connected-accounts/slack/connect';
  icon: 'add_link' | 'hub';
  labelKey: 'myConnectionsConnectGoogleAction' | 'myConnectionsConnectSlackAction';
}

export const MY_CONNECTION_CONNECT_PROVIDERS: readonly MyConnectionConnectProvider[] = [
  {
    id: 'google',
    endpoint: '/api/connected-accounts/google/connect',
    icon: 'add_link',
    labelKey: 'myConnectionsConnectGoogleAction',
  },
  {
    id: 'slack',
    endpoint: '/api/connected-accounts/slack/connect',
    icon: 'hub',
    labelKey: 'myConnectionsConnectSlackAction',
  },
];

/**
 * Filters connect entrypoints down to providers the server has OAuth
 * configured for, so a provider missing env config (e.g. Slack before its
 * prod app is provisioned) never renders a button that always 500s.
 */
export function visibleConnectProviders(
  providersConfigured: readonly string[] | undefined,
): readonly MyConnectionConnectProvider[] {
  const configured = new Set(providersConfigured ?? []);
  return MY_CONNECTION_CONNECT_PROVIDERS.filter((provider) => configured.has(provider.id));
}

export interface MyConnectionIngestPolicy {
  enabled?: boolean;
  gmail?: { exclude_labels?: string[]; [key: string]: unknown };
  calendar?: { calendars?: Record<string, { mode?: string; [key: string]: unknown }>; [key: string]: unknown };
  include_calendar_ids?: string[];
  [key: string]: unknown;
}

export interface MyConnectionAccount {
  id: string;
  connected_by_user_id: string;
  provider: MyConnectionProvider;
  provider_account_id: string;
  /** Slack の接続だけ。サーバーが Slack から読んだ名前で、保存はしていない。 */
  slack_label?: { team_name: string; user_name: string | null };
  status: MyConnectionStatus;
  created_at: string;
  ingest_policy?: MyConnectionIngestPolicy;
  visibility?: MyConnectionVisibility;
}

export interface MyConnectionIntegrationPolicyEntry {
  allowed?: boolean;
}

export type MyConnectionIntegrationsPolicy = Partial<Record<MyConnectionProvider, MyConnectionIntegrationPolicyEntry>>;

export type MyConnectionReadOnlyReason = 'revoked' | 'error' | 'integration_not_allowed';

type MyConnectionIngestLabelKey = 'connectionRowIngestLabel' | 'connectionRowCalendarIngestLabel';
type MyConnectionIngestReadbackKey =
  | 'connectionRowIngestOnReadback'
  | 'connectionRowIngestOffReadback'
  | 'connectionRowCalendarIngestOnReadback'
  | 'connectionRowCalendarIngestOffReadback';

interface MyConnectionRowBase {
  id: string;
  providerAccountId: string;
  /** 画面に出す名前。Slack はワークスペース名（と本人の名前）、読めなければ ID。 */
  accountLabel: string;
  ingestEnabled: boolean;
  ingestLabelKey: MyConnectionIngestLabelKey;
  ingestReadbackKey: MyConnectionIngestReadbackKey;
}

type MyConnectionPresentationState =
  | {
      status: 'active';
      statusIcon: 'check_circle';
      statusLabelKey: 'myConnectionsStatusActive';
      statusTone: 'success';
      access: { mode: 'editable' } | { mode: 'read_only'; reason: 'integration_not_allowed' };
    }
  | {
      status: 'revoked';
      statusIcon: 'link_off';
      statusLabelKey: 'myConnectionsStatusRevoked';
      statusTone: 'muted';
      access: { mode: 'read_only'; reason: 'revoked' };
    }
  | {
      status: 'error';
      statusIcon: 'error';
      statusLabelKey: 'myConnectionsStatusError';
      statusTone: 'danger';
      access: { mode: 'read_only'; reason: 'error' };
    };

export interface MyConnectionGmailRow extends MyConnectionRowBase {
  provider: 'gmail';
  capability: 'ingest_and_share_scope';
  icon: 'mail';
  providerLabel: 'Gmail';
  detailDescriptionKey: 'connectionRowGoogleDetailDescription';
  shareScope: { mode: 'configurable'; value: MyConnectionVisibility };
  filterLanding: {
    mode: 'static_unavailable';
    headingKey: 'connectionRowFilterHeading';
    descriptionKey: 'connectionRowFilterUnavailableDescription';
  };
}

export interface MyConnectionGoogleCalendarRow extends MyConnectionRowBase {
  provider: 'google_calendar';
  capability: 'ingest_and_share_scope';
  icon: 'calendar_month';
  providerLabel: 'Google Calendar';
  detailDescriptionKey: 'connectionRowGoogleDetailDescription';
  shareScope: { mode: 'configurable'; value: MyConnectionVisibility };
  filterLanding: {
    mode: 'static_unavailable';
    headingKey: 'connectionRowFilterHeading';
    descriptionKey: 'connectionRowFilterUnavailableDescription';
  };
}

export interface MyConnectionSlackRow extends MyConnectionRowBase {
  provider: 'slack';
  capability: 'ingest_and_share_scope';
  icon: 'hub';
  providerLabel: 'Slack';
  detailDescriptionKey: 'connectionRowSlackDetailDescription';
  shareScope: { mode: 'configurable'; value: MyConnectionVisibility };
  filterLanding: { mode: 'not_supported' };
}

export type MyConnectionRow =
  | (MyConnectionGmailRow & MyConnectionPresentationState)
  | (MyConnectionGoogleCalendarRow & MyConnectionPresentationState)
  | (MyConnectionSlackRow & MyConnectionPresentationState);

export type MyConnectionFocusTarget =
  | { type: 'disconnect'; accountId: string }
  | { type: 'connect' };

export function myConnections(
  accounts: readonly MyConnectionAccount[],
  viewerUserId: string,
): MyConnectionAccount[] {
  return accounts.filter((account) => account.connected_by_user_id === viewerUserId);
}

/**
 * 解除の単位で接続をまとめる。Gmail とカレンダーは1回の同意でつながり、解除もまとめて行われるので、
 * 同じ Google アカウントの2行を1つのグループにする。Slack は1行で1グループ。
 * グループの状態は、すべて解除済みなら revoked、どれかが error なら error、それ以外は active。
 */
export interface MyConnectionGroup {
  key: string;
  kind: 'google' | 'slack';
  providerAccountId: string;
  accountLabel: string;
  accounts: MyConnectionAccount[];
  status: MyConnectionStatus;
  /** 解除・データ削除の API に渡す行。Google はどちらの行でもペアごと処理される。 */
  actionAccountId: string;
  connectedAt: string;
}

/** Slack はワークスペース名（と本人の名前）で、Google はメールアドレスで示す。 */
export function connectionAccountLabel(account: MyConnectionAccount): string {
  const label = account.provider === 'slack' ? account.slack_label : undefined;
  if (!label?.team_name) return account.provider_account_id;
  return label.user_name ? `${label.team_name} · ${label.user_name}` : label.team_name;
}

const GOOGLE_PROVIDER_ORDER: Record<MyConnectionProvider, number> = { gmail: 0, google_calendar: 1, slack: 2 };

export function groupMyConnections(accounts: readonly MyConnectionAccount[]): MyConnectionGroup[] {
  const groups = new Map<string, MyConnectionGroup>();
  for (const account of accounts) {
    const kind = account.provider === 'slack' ? 'slack' : 'google';
    const key = kind === 'google'
      ? `google:${account.provider_account_id.trim().toLowerCase()}`
      : `slack:${account.id}`;
    const group = groups.get(key);
    if (group) group.accounts.push(account);
    else {
      groups.set(key, {
        key,
        kind,
        providerAccountId: account.provider_account_id,
        accountLabel: connectionAccountLabel(account),
        accounts: [account],
        status: account.status,
        actionAccountId: account.id,
        connectedAt: account.created_at,
      });
    }
  }
  return [...groups.values()].map((group) => {
    const ordered = [...group.accounts].sort((a, b) => GOOGLE_PROVIDER_ORDER[a.provider] - GOOGLE_PROVIDER_ORDER[b.provider]);
    const status: MyConnectionStatus = ordered.every((account) => account.status === 'revoked')
      ? 'revoked'
      : ordered.some((account) => account.status === 'error') ? 'error' : 'active';
    const connectedAt = ordered.map((account) => account.created_at).sort()[0] ?? group.connectedAt;
    return { ...group, accounts: ordered, status, actionAccountId: ordered[0].id, connectedAt };
  });
}

/**
 * Removes disclosure state for rows that are no longer viewer-visible.
 * Always returns a fresh Set so Lit observes the state change.
 */
export function cleanExpandedConnectionIds(
  expandedIds: ReadonlySet<string>,
  visibleIds: readonly string[],
): Set<string> {
  const visible = new Set(visibleIds);
  return new Set([...expandedIds].filter((id) => visible.has(id)));
}

/** Selects the closest predictable focus destination after a row is removed. */
export function disconnectFocusTarget(
  previousAccountIds: readonly string[],
  removedAccountId: string,
): MyConnectionFocusTarget {
  const removedIndex = previousAccountIds.indexOf(removedAccountId);
  if (removedIndex < 0) return { type: 'connect' };

  const nextId = previousAccountIds[removedIndex + 1];
  if (nextId) return { type: 'disconnect', accountId: nextId };

  const previousId = previousAccountIds[removedIndex - 1];
  if (previousId) return { type: 'disconnect', accountId: previousId };

  return { type: 'connect' };
}

function presentationStateFor(
  account: MyConnectionAccount,
  integrationAllowed: boolean,
): MyConnectionPresentationState {
  // Connection health wins over workspace policy so the UI explains the
  // condition that the user must address first.
  // revoked は本人（またはメンバーの終了）が解除した状態で、エラーではない。
  if (account.status === 'revoked') {
    return {
      status: 'revoked',
      statusIcon: 'link_off',
      statusLabelKey: 'myConnectionsStatusRevoked',
      statusTone: 'muted',
      access: { mode: 'read_only', reason: 'revoked' },
    };
  }
  if (account.status === 'error') {
    return {
      status: 'error',
      statusIcon: 'error',
      statusLabelKey: 'myConnectionsStatusError',
      statusTone: 'danger',
      access: { mode: 'read_only', reason: 'error' },
    };
  }
  return {
    status: 'active',
    statusIcon: 'check_circle',
    statusLabelKey: 'myConnectionsStatusActive',
    statusTone: 'success',
    access: integrationAllowed
      ? { mode: 'editable' }
      : { mode: 'read_only', reason: 'integration_not_allowed' },
  };
}

export function presentMyConnectionRow(
  account: MyConnectionAccount,
  integrationsPolicy: MyConnectionIntegrationsPolicy = {},
): MyConnectionRow {
  const ingestEnabled = account.ingest_policy?.enabled ?? true;
  const integrationAllowed = integrationsPolicy[account.provider]?.allowed ?? true;
  const common = {
    id: account.id,
    providerAccountId: account.provider_account_id,
    accountLabel: connectionAccountLabel(account),
    ingestEnabled,
    ...presentationStateFor(account, integrationAllowed),
  };

  if (account.provider === 'gmail') {
    return {
      ...common,
      provider: 'gmail',
      capability: 'ingest_and_share_scope',
      icon: 'mail',
      providerLabel: 'Gmail',
      ingestLabelKey: 'connectionRowIngestLabel',
      ingestReadbackKey: ingestEnabled ? 'connectionRowIngestOnReadback' : 'connectionRowIngestOffReadback',
      detailDescriptionKey: 'connectionRowGoogleDetailDescription',
      shareScope: { mode: 'configurable', value: account.visibility ?? 'shared' },
      filterLanding: {
        mode: 'static_unavailable',
        headingKey: 'connectionRowFilterHeading',
        descriptionKey: 'connectionRowFilterUnavailableDescription',
      },
    };
  }

  if (account.provider === 'google_calendar') {
    return {
      ...common,
      provider: 'google_calendar',
      capability: 'ingest_and_share_scope',
      icon: 'calendar_month',
      providerLabel: 'Google Calendar',
      ingestLabelKey: 'connectionRowCalendarIngestLabel',
      ingestReadbackKey: ingestEnabled
        ? 'connectionRowCalendarIngestOnReadback'
        : 'connectionRowCalendarIngestOffReadback',
      detailDescriptionKey: 'connectionRowGoogleDetailDescription',
      shareScope: { mode: 'configurable', value: account.visibility ?? 'shared' },
      filterLanding: {
        mode: 'static_unavailable',
        headingKey: 'connectionRowFilterHeading',
        descriptionKey: 'connectionRowFilterUnavailableDescription',
      },
    };
  }

  return {
    ...common,
    provider: 'slack',
    capability: 'ingest_and_share_scope',
    icon: 'hub',
    providerLabel: 'Slack',
    ingestLabelKey: 'connectionRowIngestLabel',
    ingestReadbackKey: ingestEnabled ? 'connectionRowIngestOnReadback' : 'connectionRowIngestOffReadback',
    detailDescriptionKey: 'connectionRowSlackDetailDescription',
    shareScope: { mode: 'configurable', value: account.visibility ?? 'shared' },
    filterLanding: { mode: 'not_supported' },
  };
}
